import { images } from './images';
import { Brain, Activity, Zap, User, AlertCircle, Shield, Moon, Move } from 'lucide-react';

export const doctorInfo = {
  name: "Dr. Shafiqur Rehman Mohammed",
  qualifications: ["MBBS", "MD (Internal Medicine)", "DM (Neurology)", "MRCP UK SCE (Neurology)"],
  title: "Consultant Neurologist",
  location: "NMC Specialty Hospital, Al Nahda, Dubai",
  clinic: "NMC Specialty Hospital",
  phone: "0521657940",
  email: "shafiqneurologist@gmail.com",
  social: {
    // facebook: "https://www.facebook.com/",
    linkedin: "https://www.linkedin.com/in/dr-shafiqur-mohammed-md-dm-neurology-sce-mrcp-sce-certified-neurology-290428a9/?utm_source=share_via&utm_content=profile&utm_medium=member_android",
    instagram: "https://www.instagram.com/dr.shafiqdubai?utm_source=qr&igsh=MWY0ejh0cTc0NHlocA==",
    youtube: "https://www.youtube.com/@DrShafiqurNeurologist",
    whatsapp: "https://api.whatsapp.com/send/?phone=971521657940&text&app_absent=0",
   // google: "https://g.page/"
  }
};

export const heroContent = {
  title: "Advanced Neurological Care for a Better Quality of Life",
  subtitle: "Expert diagnosis and personalized treatment plans for complex neurological conditions by Dr. Shafiqur Rehman Mohammed.",
  cta: "Book an Appointment"
};

export const aboutContent = {
  title: "About Dr. Shafiqur Rehman Mohammed",
  description: `Dr. Shafiqur Rehman Mohammed is a Consultant Neurologist practicing at NMC Specialty Hospital, Al Nahda, Dubai, and is recognized as one of the leading neurologists in the region. With over 20 years of clinical experience, he is an expert in treating acute, chronic, and complex neurological disorders.

  He completed his M.B.B.S. from Osmania Medical College, Hyderabad, and earned his M.D. in Internal Medicine from Kakatiya Medical College, Warangal. He further specialized with a D.M. in Neurology from the prestigious Nizam’s Institute of Medical Sciences (NIMS), Hyderabad, where he also served as a Senior Resident for 3 years. Additionally, he holds the MRCP UK SCE certification in Neurology.

  Before moving to Dubai, Dr. Shafiqur served as a Consultant Neurologist in Hyderabad and as an Assistant Professor of Neurology at Deccan College of Medical Sciences, training future neurologists. For the past 6 years, he has been associated with NMC Specialty Hospital, Al Nahda, Dubai, serving as the H.O.D. of Neurology.

  Fluent in English, Hindi, Urdu, and Telugu, Dr. Shafiqur combines clinical excellence with compassionate care, ensuring each patient receives personalized treatment. He is a life member of the Indian Academy of Neurology and the Andhra Pradesh Neuroscientists Association.`,
  mission: "To provide compassionate, evidence-based neurological care that empowers patients to lead healthier, fuller lives."
};

export const services = [
  {
    id: "headache",
    slug: "headache-migraine",
    title: "Headache & Migraine",
    description: "Specialized treatment for chronic headaches and migraines to alleviate pain and prevent future episodes.",
    longDescription: "Chronic headaches and migraines can severely impact productivity and quality of life. Dr. Shafiqur employs a systematic approach to diagnose the specific type of headache—whether it's migraine, tension-type, cluster, or medication-overuse headache. Our goal is not just to treat the pain but to identify triggers and implement preventive strategies.",
    symptoms: [
      "Throbbing or pulsing pain, usually on one side of the head",
      "Sensitivity to light and sound",
      "Nausea and vomiting",
      "Visual disturbances (aura)",
      "Tightness or pressure around the forehead"
    ],
    treatment: [
      "Abortive medications for acute attacks",
      "Preventive pharmacotherapy",
      "Botox injections for chronic migraine",
      "Lifestyle modification and trigger management",
      "Stress management techniques"
    ],
    image: images.services.headache,
    icon: Brain,
    details: "Not all headaches are the same. We specialize in diagnosing the root cause and provide targeted therapies including medication and Botox therapy.",
    videoUrl: "https://youtube.com/shorts/SoToseOUGm4?si=Xto6oFble9sCTF3r"
  },
  {
    id: "vertigo",
    slug: "vertigo-balance",
    title: "Dizziness & Vertigo",
    description: "Expert care for dizziness and balance disorders to restore stability and confidence.",
    longDescription: "Vertigo is more than just a fear of heights; it's a sensation of spinning that can be caused by inner ear or neurological problems. Dr. Shafiqur performs detailed vestibular assessments to distinguish between peripheral causes (like BPPV) and central causes (like stroke or MS). Treatment is targeted to stop the spinning and restore balance.",
    symptoms: [
      "Spinning sensation (Vertigo)",
      "Loss of balance or unsteadiness",
      "Nausea and vomiting",
      "Ringing in the ears (Tinnitus)",
      "Difficulty focusing the eyes"
    ],
    treatment: [
      "Canalith repositioning maneuvers (e.g., Epley maneuver)",
      "Vestibular rehabilitation therapy",
      "Medications for motion sickness and nausea",
      "Balance training exercises",
      "Management of underlying migraines or infections"
    ],
    image: images.services.vertigo,
    icon: Activity,
    details: "Vertigo can significantly impact daily life. We perform detailed vestibular assessments and offer repositioning maneuvers and medical management.",
    videoUrl: "https://youtube.com/shorts/SdshTR5DdgU?si=uREz0csJMd-smu6N"
  },
  {
    id: "stroke",
    slug: "stroke-management",
    title: "Acute Stroke Care",
    description: "Rapid assessment and comprehensive management of acute stroke and vascular disorders.",
    longDescription: "Stroke is a life-altering event that requires immediate and expert intervention. Dr. Shafiqur specializes in the acute management of ischemic and hemorrhagic strokes, focusing on minimizing brain damage during the critical 'golden hour'. Beyond acute care, our clinic provides a robust long-term rehabilitation program designed to help survivors regain lost skills and achieve the highest possible level of independence.",
    symptoms: [
      "Sudden numbness or weakness in the face, arm, or leg",
      "Confusion, trouble speaking, or difficulty understanding speech",
      "Sudden trouble seeing in one or both eyes",
      "Sudden trouble walking, dizziness, loss of balance",
      "Severe headache with no known cause"
    ],
    treatment: [
      "Thrombolytic therapy (clot-busting drugs)",
      "Mechanical thrombectomy referral",
      "Post-stroke rehabilitation (Physical, Occupational, Speech therapy)",
      "Secondary prevention strategies (Blood pressure, cholesterol management)"
    ],
    image: images.services.stroke,
    icon: Activity,
    details: "Stroke is a medical emergency. We offer immediate intervention protocols and long-term rehabilitation strategies to help patients recover lost functions.",
    videoUrl: "https://youtube.com/shorts/SdshTR5DdgU?si=uREz0csJMd-smu6N"
  },
  {
    id: "epilepsy",
    slug: "epilepsy-care",
    title: "Epilepsy Management",
    description: "Advanced seizure management and ongoing support for patients living with epilepsy.",
    longDescription: "Epilepsy is a neurological disorder characterized by recurrent seizures. Dr. Shafiqur provides comprehensive care for patients of all ages, from diagnosis using advanced EEG monitoring to personalized medication management. We strive to achieve 'seizure freedom' with minimal side effects, allowing patients to lead normal, active lives.",
    symptoms: [
      "Temporary confusion",
      "Staring spells",
      "Uncontrollable jerking movements of the arms and legs",
      "Loss of consciousness or awareness",
      "Psychic symptoms such as fear or anxiety"
    ],
    treatment: [
      "Anti-epileptic drugs (AEDs) tailored to seizure type",
      "Dietary therapies (e.g., Ketogenic diet)",
      "Vagus Nerve Stimulation (VNS)",
      "Surgical evaluation for drug-resistant epilepsy",
      "Lifestyle counseling for seizure safety"
    ],
    image: images.services.epilepsy,
    icon: Zap,
    details: "Living with epilepsy requires careful management. We provide precise diagnosis and personalized medication plans to control seizures.",
    videoUrl: "https://youtube.com/shorts/tnrJ8zNNI8c?si=Szfj2NUpEQcAQmjx"
  },
  {
    id: "backpain",
    slug: "neck-back-pain",
    title: "Neck & Back Pain",
    description: "Non-surgical management of spinal pain, disc herniation, and spinal cord diseases.",
    longDescription: "Neck and back pain are among the most common reasons for medical visits. While often caused by muscle strain, they can also indicate disc herniation, spinal stenosis, or other neurological issues. Dr. Shafiqur emphasizes a conservative, non-surgical approach first, utilizing medication, physiotherapy, and lifestyle ergonomics to relieve pain and restore mobility.",
    symptoms: [
      "Muscle ache",
      "Shooting or stabbing pain",
      "Pain that radiates down the leg (Sciatica) or arm",
      "Limited flexibility or range of motion",
      "Numbness or tingling in extremities"
    ],
    treatment: [
      "Pharmacotherapy (Muscle relaxants, pain relievers)",
      "Physical therapy and strengthening exercises",
      "Ergonomic assessments and lifestyle advice",
      "Trigger point injections",
      "Referral for surgical evaluation if conservative measures fail"
    ],
    image: images.services.backpain,
    icon: User,
    details: "Chronic neck and back pain often stem from posture or degenerative changes. We prioritize conservative management and ergonomic advice.",
    videoUrl: "https://youtube.com/shorts/0heiWOz3JVc?si=HWJ-fuyVEjq4MGA1"
  },
  {
    id: "neuropathy",
    slug: "neuropathy-muscle",
    title: "Nerve & Muscle Diseases",
    description: "Diagnosis and treatment of neuropathy, myopathy, and nerve damage causing weakness.",
    longDescription: "Peripheral neuropathy and muscle diseases (myopathy) result from damage to the nerves or muscle fibers. They often cause weakness, numbness, and pain. Dr. Shafiqur focuses on identifying the underlying cause—such as diabetes, vitamin deficiencies, or autoimmune conditions—to treat the root of the problem while effectively managing symptoms.",
    symptoms: [
      "Gradual onset of numbness, prickling or tingling in feet or hands",
      "Sharp, jabbing, throbbing or burning pain",
      "Muscle weakness or wasting",
      "Extreme sensitivity to touch",
      "Lack of coordination and falling"
    ],
    treatment: [
      "Management of underlying conditions (e.g., blood sugar control)",
      "Pain relief medications (antidepressants, anticonvulsants)",
      "Immunotherapy for autoimmune causes",
      "Physical therapy to improve strength and balance",
      "Transcutaneous electrical nerve stimulation (TENS)"
    ],
    image: images.services.neuropathy,
    icon: Activity,
    details: "Nerve and muscle diseases can be debilitating. We focus on identifying causes and managing symptoms to prevent further damage.",
    videoUrl: "https://youtube.com/shorts/xuJds0ryg_4?si=rrMHsoO6ldl_J0qj"
  },
  {
    id: "neuroimmunology",
    slug: "neuro-immunology",
    title: "Neuro-Immunology",
    description: "Specialized care for MS, Myasthenia Gravis, and other inflammatory neurological diseases.",
    longDescription: "Neuro-immunological disorders occur when the immune system attacks the nervous system. Conditions like Multiple Sclerosis (MS), Myasthenia Gravis, and Vasculitis require specialized, long-term management. Dr. Shafiqur provides expert care using the latest immunomodulatory therapies to prevent relapses and disability.",
    symptoms: [
      "Vision problems (blurring, double vision)",
      "Muscle weakness and fatigue",
      "Balance and coordination issues",
      "Numbness or tingling",
      "Cognitive changes"
    ],
    treatment: [
      "Disease-modifying therapies (DMTs) for MS",
      "Corticosteroids and immunosuppressants",
      "Intravenous immunoglobulin (IVIg)",
      "Plasmapheresis",
      "Symptomatic management for fatigue and spasticity"
    ],
    image: images.services.myasthenia,
    icon: Shield,
    details: "We offer comprehensive management for autoimmune neurological conditions, focusing on preserving function and preventing progression.",
    videoUrl: "https://www.youtube.com/shorts/jlvyY2sYimI"
  },
  {
    id: "movement",
    slug: "movement-disorders",
    title: "Movement Disorders",
    description: "Expert management of Parkinson's disease, tremors, and other movement disorders.",
    longDescription: "Movement disorders affect the ability to control movement, leading to either too much movement (tremors, tics) or too little (Parkinson's). Dr. Shafiqur specializes in the diagnosis and medical management of these conditions, helping patients maintain their mobility and quality of life through tailored medication regimens.",
    symptoms: [
      "Tremors or shaking (often at rest)",
      "Slowness of movement (Bradykinesia)",
      "Stiffness or rigidity of limbs",
      "Balance problems",
      "Involuntary muscle contractions (Dystonia)"
    ],
    treatment: [
      "Dopaminergic medications (Levodopa)",
      "Dopamine agonists",
      "Anticholinergics for tremors",
      "Botox injections for dystonia",
      "Physical and occupational therapy"
    ],
    image: images.services.neuralgia,
    icon: Move,
    details: "Movement disorders require precise medication adjustment. We work closely with patients to optimize their therapy and improve mobility.",
    videoUrl: "https://youtube.com/shorts/TJ_OKXbG2Uk?si=DOjUEVHh9h8qqmql"
  },
  {
    id: "memory",
    slug: "memory-sleep",
    title: "Memory & Sleep Disorders",
    description: "Evaluation and treatment of dementia, memory loss, and sleep-related neurological issues.",
    longDescription: "Memory loss and sleep disorders often go hand-in-hand and can significantly affect daily functioning. Dr. Shafiqur provides comprehensive evaluation for dementia (including Alzheimer's) and treats sleep disorders that may have a neurological basis. Early diagnosis and supportive care are key to managing these conditions.",
    symptoms: [
      "Short-term memory loss",
      "Difficulty finding words",
      "Confusion about time or place",
      "Insomnia or excessive daytime sleepiness",
      "Acting out dreams (REM sleep behavior disorder)"
    ],
    treatment: [
      "Cognitive enhancers (Cholinesterase inhibitors)",
      "Management of behavioral symptoms",
      "Sleep hygiene education",
      "Medications for sleep regulation",
      "Caregiver support and counseling"
    ],
    image: images.services.headache,
    icon: Moon,
    details: "We provide compassionate care for memory and sleep disorders, focusing on improving quality of life for both patients and caregivers.",
    videoUrl: "https://youtube.com/shorts/SdshTR5DdgU?si=uREz0csJMd-smu6N"
  }
];

export const testimonials = [
  {
    name: "Nalukwago Ritah",
    text: "I take this opportunity to thank the NMC team especially Dr. Sonalica Suresh and her team, Dr. Rehman Shafiqur for my wellbeing, me and my family we really appreciate all the services. I strongly recommend this Hospital. NMC Al Nahda.”",
    rating: 5
  },
  {
    name: "Afnan Salam",
    text: "Very patient and attentive doctor. Carefully listened to a 10 year long eventful medical history of recurrent strokes and MI and explained his approach in simple terms and good details. He was very receptive to our considerations and doubts. An affordable and brilliant neurologist in this area. Thankyou",
    rating: 5
  },
  {
    name: "Narasimha Reddy",
    text: "My father has been under Dr. Shafiqur's care for Parkinson's. We have seen great improvement in his mobility. A truly dedicated doctor.",
    rating: 5
  },
  {
    name: "Harish",
    text: "I was suffering from severe vertigo for months. Dr. Shafiqur diagnosed the issue correctly and within weeks I was back to normal. Thank you doctor!",
    rating: 5
  },
  {
    name: "G Mallaiah",
    text: "Very knowledgeable and patient-friendly. He listens to all your concerns and explains the treatment plan in detail. Best neurologist in Al Nahda.",
    rating: 5
  },
  {
    name: "Prasad K",
    text: "Excellent care for my mother's stroke recovery. The rehabilitation guidance provided by Dr. Shafiqur was instrumental in her progress.",
    rating: 5
  },
  {
    name: "Mallik ",
    text: "I recommend this doctor.he is very friendly and explains the problem in detail and he knows exactly what tests to be done for what problem.He is the expert in nuerology , he will make you understand what your problem is exactly.I have been visiting him regularly monthly once right from the day I have got the brain stroke in Jan 2016 ( @ age of 31) .He is the GOD for me.thanks a lot sir for treating me like your brother.",
    rating: 5
  }
];

export const faqs = [
  {
    question: "Does a stroke always guarantee disability?",
    answer: "Not necessarily. Stroke, when treated within the 'golden hour' (first 4.5 hours), can not only save your life but can also significantly reduce or prevent long-term disability. Immediate medical attention is crucial."
  },
  {
    question: "Is every memory loss dementia?",
    answer: "No, memory loss can be due to various reasons including stress, vitamin deficiencies, thyroid issues, or depression. Dementia is a specific progressive condition. A proper neurological evaluation can determine the cause."
  },
  {
    question: "Is there a cure for constant neck pain?",
    answer: "Yes, in most cases. The root cause—often posture, muscle strain, or disc issues—needs to be identified. We focus on treating the cause through medication, physiotherapy, and lifestyle changes to ensure long-term relief."
  },
  {
    question: "Are headache and migraine medicines the same?",
    answer: "No. Migraines are a specific type of neurological condition that may require specialized abortive and preventive medications. General painkillers might not work effectively for migraines and can sometimes cause medication-overuse headaches."
  }
];

export const healthTips = [
  "Exercise regularly to improve blood flow to the brain.",
  "Get enough sleep (7-8 hours) to allow your brain to detoxify.",
  "Eat a balanced diet rich in antioxidants and omega-3 fatty acids.",
  "Stay mentally active with puzzles, reading, or learning new skills.",
  "Remain socially involved to prevent depression and cognitive decline."
];
